import { PaymentSystemPublic } from "@scrile/api-provider/dist/api/PaymentSystemsProvider";
import { PurchaseSubjectTypeEnum } from "@scrile/api-provider/dist/api/PurchaseTypeProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import { Subscription } from "@scrile/api-provider/dist/api/SubscriptionProvider";
import { Transaction } from "@scrile/api-provider/dist/api/TransactionProvider";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import {
  createRejectError,
  makePurchase,
  PAYMENT_ERROR_MESSAGES,
  TIMEOUT_TO_CLOSE,
} from "@/payments/integrations/utils";
import paymentComponent from "./AuthorizeNet.vue";
import { IntegrationModule } from "../types";

interface PaymentData {
  purchaseSubject?: Subject<PurchaseSubjectTypeEnum>;
}

const integrationModule: IntegrationModule<PaymentData> = {
  paymentComponent,
  purchase: async (data, billingDetails) => {
    const transaction: Transaction = await providers.AuthorizeNetPurchaseProvider.purchaseDeposit({
      data: { ...data, subject: billingDetails?.purchaseSubject },
    });
    return {
      status: true,
      subjectPurchased: !!billingDetails?.purchaseSubject,
      transaction,
    };
  },
  subscribe: async data => {
    const subscription: Subscription = await providers.AuthorizeNetPurchaseProvider.purchaseRecurring({ data });
    return {
      status: true,
      subjectPurchased: false,
      subscription,
    };
  },
  assign: async data =>
    new Promise(async (resolve, reject) => {
      try {
        let timeout: ReturnType<typeof setTimeout>;
        const subscription = await providers.CreditCardAssignmentsProvider.subscribeCreditCardAssignmentCreated(
          card => {
            timeout && clearTimeout(timeout);
            subscription.unsubscribe();
            resolve({ status: true, card });
          }
        );
        try {
          const urlSuccess = new URL("/payments/authorizeNet/thankyou.html", location.origin);
          urlSuccess.searchParams.set("result", "success");

          const getUrl = async () => {
            const response = await providers.AuthorizeNetPurchaseProvider.generateProfileFormData({
              data: {
                successUrl: urlSuccess.toString(),
                paymentSystemId: data.paymentSystemId,
              },
            });
            const urlAssign = new URL("/payments/authorizeNet/assign.html", location.origin);
            urlAssign.searchParams.append("url", encodeURIComponent(response.url));
            urlAssign.searchParams.append("token", encodeURIComponent(response.token));
            return urlAssign.toString();
          };

          await makePurchase(getUrl).catch(e => {
            if (e.message === PAYMENT_ERROR_MESSAGES.VERIFY_TRANSACTION) {
              timeout = setTimeout(() => {
                subscription.unsubscribe();
                reject(createRejectError(e));
              }, TIMEOUT_TO_CLOSE);
            } else throw e;
          });
        } catch (e) {
          subscription.unsubscribe();
          reject(createRejectError(e));
        }
      } catch (e) {
        reject(createRejectError(e));
      }
    }),
  allowSaveCard: true,
  name: $t("Authorize.net"),
  logo: "/img/paymentSystems/authorizeNet.png",
};

export const selector = (system: PaymentSystemPublic | null): boolean => {
  return system?.type === "authorize-net" ?? false;
};

export default integrationModule;
